import { Alert, Button, Space, Spin } from 'antd';
import firebase from 'firebase/app';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';

import { ProgressConfigType } from '../../components/OfferRelatedWrapper/OfferProgress';
import { OfferRelatedWrapper } from '../../components/OfferRelatedWrapper/OfferRelatedWrapper';
import { ToastVariant } from '../../components/Toast/toast.model';
import { toastService } from '../../components/Toast/toast.service';
import { onLogOfferAddConfirm } from '../../events/hooks/onLogOfferAddConfirm.hook';
import { firebaseFunctions } from '../../firebase/firebase';
import { FirebaseCallableFunctions, FunctionAddOfferPayload } from '../../firebase/firebase.models';
import { useGetOffer } from '../../firebase/hooks/getOfferById.hook';
import { OfferCheck, useUpdateOfferCheck } from '../../firebase/hooks/updateOfferCheck.hook';
import { useGetOfferImages } from '../../hooks/getOfferImages.hook';
import { OfferStatusState } from '../../models/offers.model';
import { App } from '../App/App';
import { useAuth } from '../Auth/AuthContext';
import { AppRoutes } from '../Routing/routing.model';
import { ManualSignatureUpload } from '../Shared/ManualSignatureUpload';

import { createPDFs } from './Documents/createPDFs';
import { ConfigType, FilePaths } from './Documents/documents.models';

interface Props {
  offerId: string;
}

export const OfferAddConfirm: React.FC<Props> = ({ offerId }) => {
  const { isAuthenticating, isAuth } = useAuth();
  return <App>{!isAuthenticating && isAuth ? <WhenAuth offerId={offerId} /> : <WhenNotAuth />}</App>;
};

const WhenAuth: React.FC<Props> = ({ offerId }) => {
  const { push } = useHistory();
  const { currentUser } = useAuth();

  const isLoading = useRef(false);
  const [offer] = useGetOffer(offerId);
  const imagesUrl = useGetOfferImages(offerId);

  const [errorMessage, setErrorMessage] = useState('');
  const [showLoadingOffer, setShowLoadingOffer] = useState(true);
  const [showManualUploadUI, setShowManualUploadUI] = useState(false);
  const [certificateFileExists, setCertificateFileExists] = useState(false);
  const [checkingFile, setCheckingFile] = useState(true);
  const [uploadCompleted, setUploadCompleted] = useState(false);
  const { t } = useTranslation();

  const handleOfferConfirmation = async () => {
    try {
      // Fake step: Sprawdzanie bezpieczeństwa sieci
      await useUpdateOfferCheck(offer.id, OfferCheck.CHECKING_NETWORK);
      await simulateStep(offerId, OfferCheck.DOWNLOADING_DATA, 2000);

      // Real step: Podpisywanie oferty
      await useUpdateOfferCheck(offer.id, OfferCheck.SIGNING_OFFER);

      // Generuj certyfikaty (backend pominie fixedlong > 12, bo user uploadował)
      await createPDFs(offer, ConfigType.OFFER);

      // step
      await simulateStep(offerId, OfferCheck.SAVE_REPORT, 2300);
      onLogOfferAddConfirm(offerId);

      // Real step: Wysyłanie oferty
      await useUpdateOfferCheck(offer.id, OfferCheck.SENDING_OFFER);
      const onOfferAddConfirm = firebaseFunctions.httpsCallable(FirebaseCallableFunctions.ON_OFFER_ADD_CONFIRM);
      const functionSignOfferConfirmPayload: FunctionAddOfferPayload = { offerId };
      await onOfferAddConfirm(functionSignOfferConfirmPayload);
      await useUpdateOfferCheck(offer.id, OfferCheck.DONE);
    } catch (error: any) {
      handleError(error);
    }
  };

  const handleError = (error: any) => {
    console.error('Error during offer confirmation:', error);
    setErrorMessage(error.message);
    toastService.show(error.message, t('ERROR_DESCRIPTION'), {
      variant: ToastVariant.ERROR,
    });
    push(AppRoutes.HOME);
  };

  // Sprawdź czy certyfikat już istnieje w storage
  useEffect(() => {
    const checkCertificateExists = async () => {
      if (!offer || !currentUser) {
        return;
      }

      // Nie sprawdzaj po uploadzie - proces trwa w tle
      if (uploadCompleted) {
        return;
      }

      // Tylko dla fixedlong > 12 miesięcy
      if (!offer.leaseAgreement.fixedlong || offer.leaseAgreement.fixedlongMonths <= 12) {
        setCheckingFile(false);
        return;
      }

      try {
        setCheckingFile(true);

        // Sprawdź czy plik istnieje w storage
        const storage = firebase.storage();
        const filePath = `offers/${offerId}/${FilePaths.CERTYFIKAT_POWYZEJ_12}`;
        const fileRef = storage.ref(filePath);

        try {
          await fileRef.getDownloadURL();
          // Plik istnieje
          setCertificateFileExists(true);
        } catch (error: any) {
          // Plik nie istnieje (error.code === 'storage/object-not-found')
          setCertificateFileExists(false);
        }
      } catch (error) {
        console.error('Error checking certificate file:', error);
        setCertificateFileExists(false);
      } finally {
        setCheckingFile(false);
      }
    };

    checkCertificateExists();
  }, [offer, offerId, currentUser, uploadCompleted]);

  useEffect(() => {
    if (!offer) {
      setShowLoadingOffer(true);
      return;
    }

    setShowLoadingOffer(false);

    if (offerId === undefined) {
      push(AppRoutes.HOME, { offerId });
      return;
    }

    // Fixedlong > 12 miesięcy
    if (
      offer.offerStatus.state === OfferStatusState.UNSIGNED &&
      offer.leaseAgreement.fixedlong &&
      offer.leaseAgreement.fixedlongMonths > 12
    ) {
      // Jeśli upload został zakończony, nie rób nic - proces już trwa
      if (uploadCompleted) {
        return;
      }

      // Jeśli obecnie pokazujemy UI uploadu, nie rób nic - czekamy na akcję użytkownika
      if (showManualUploadUI) {
        return;
      }

      // Jeśli plik już istnieje i NIE pokazujemy UI uploadu, wyślij maila (użytkownik wrócił po uploadzie)
      if (!checkingFile && certificateFileExists && !isLoading.current) {
        isLoading.current = true;
        handleOfferConfirmation();
        return;
      }

      // Jeśli plik nie istnieje, pokaż UI uploadu
      if (!checkingFile && !certificateFileExists) {
        setShowManualUploadUI(true);
        return;
      }

      // W innych przypadkach (np. checkingFile = true) czekaj
      return;
    }

    // Standardowy flow - uruchom handleOfferConfirmation
    if (offer.offerStatus.state === OfferStatusState.UNSIGNED && !isLoading.current) {
      isLoading.current = true;
      handleOfferConfirmation();
    }
  }, [offer, offerId, push, checkingFile, certificateFileExists, uploadCompleted]);

  // Pokazuj ManualSignatureUpload UI gdy wykryto fixedlong
  if (showManualUploadUI && offer) {
    return (
      <ManualSignatureUpload
        offerId={offerId}
        offer={offer}
        userRole="landlord"
        sourceFileName={FilePaths.PROJEKT_POWYZEJ_12}
        targetFileName={FilePaths.CERTYFIKAT_POWYZEJ_12}
        onUploadComplete={async () => {
          setUploadCompleted(true);
          setShowManualUploadUI(false);
          isLoading.current = true;
          // Po uploadzie - wyślij maila i aktywuj ofertę
          await handleOfferConfirmation();
        }}
      />
    );
  }

  if (showLoadingOffer) {
    return (
      <Space
        direction="vertical"
        style={{
          width: '100%',
          height: '50vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}>
        <Spin size="large" />
      </Space>
    );
  }

  if (offer && offer.offerStatus.state !== OfferStatusState.UNSIGNED && !isLoading.current && !uploadCompleted) {
    return (
      <Space
        direction="vertical"
        style={{
          width: '100%',
          marginTop: '100px',
          marginBottom: '60%',
          padding: '50px',
        }}>
        <Alert
          message={t('OFFER_ALREADY_ACTIVE')}
          description={t('OFFER_VIEW_DESCRIPTION')}
          type="info"
          action={
            <Button size="small" type="primary" onClick={() => push(AppRoutes.OFFER.replace(':id', offerId))}>
              {t('SEE')}
            </Button>
          }
        />
      </Space>
    );
  }

  if (offer) {
    return (
      <OfferRelatedWrapper
        images={imagesUrl}
        offer={offer}
        description={t('RENTAL_OFFER_POSTED')}
        error={errorMessage}
        progressConfigType={ProgressConfigType.OFFER}
      />
    );
  }

  return <div>{t('ERROR_DESCRIPTION')}</div>;
};

// Funkcja symulująca kroki z opóźnieniem
export async function simulateStep(offerId: string, step: OfferCheck, duration: number): Promise<void> {
  await useUpdateOfferCheck(offerId, step);
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve();
    }, duration);
  });
}

const WhenNotAuth: React.FC = () => {
  const { showSignInModal, closeModal } = useAuth();

  useEffect(() => {
    showSignInModal();
    return () => closeModal();
  }, [showSignInModal, closeModal]);

  return null;
};
